import { Component, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { EventEmitter } from '@angular/forms/src/facade/async';
import { ColumnApi, GridOptions } from 'ag-grid';
import { CountyService } from '../../../../services/county.service';
import { ProvidersService } from '../../../providers/providers.service';
import { Subscription } from 'rxjs/Subscription';
import { T2LoadingService } from '../../../../trip2-theme/components/t2-loading/t2-loading.service';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { SweetAlertHelper } from '../../../../core/helpers/sweet-alert-helper.service';
import { CountyHelper } from 'app/core/helpers/county-helper.service';

@Component({
  selector: 'app-create-county',
  templateUrl: './create-county.component.html',
  styleUrls: ['./create-county.component.scss'],
})

export class CreateCountyComponent implements OnInit, OnChanges, OnDestroy {
  @Output() cancel?: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Input() countySelected?: any;

  subscriptions: Subscription[] = [];

  gridColumnApi: ColumnApi;
  gridZips: GridOptions;

  formCounty: FormGroup;

  constructor(
    private serviceCountyService: CountyService,
    private fb: FormBuilder,
    protected providerService: ProvidersService,
    private loading: T2LoadingService,
    private sweetAlertHelper: SweetAlertHelper,
    private countyHelper: CountyHelper,
  ) {
    this.createGrid();
    this.createForm();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['countySelected']) {
      if (this.countySelected !== null) {
        this.getCountyData();
      } else {
        this.resetForm();
      }
    }
  }

  ngOnInit() {}

  createForm() {
    this.formCounty = this.fb.group({
      countyid: [''],
      description: ['', Validators.required],
      region: [''],
      letterregion: ['', Validators.required],
      isactive: [true],
      countyzipcodes: this.fb.array([]),
    });
  }

  getCountyData() {
    this.resetForm();
    this.getCounty(this.countySelected.countyid);
  }

  get isEdit(): string {
    return this.formCounty.get('description').value;
  }

  get countyzipcodes(): FormArray {
    return this.formCounty.get('countyzipcodes') as FormArray;
  }

  validateRegion(): void {
    const regionControl = this.formCounty.get('letterregion');
    regionControl.setValue(this.countyHelper.validateRegion(regionControl.value));
  }

  newzipCode(data: any) {
    return this.fb.group({
      countyzipcodeid: data.countyzipcodeid ? data.countyzipcodeid : 0,
      zipcode: data.zipcode ? data.zipcode : '',
    });
  }

  addZipCode(zipCode: number) {
    if (zipCode) {
      const data = {
        countyzipcodeid: 0,
        zipcode: zipCode,
      };
      this.countyzipcodes.push(this.newzipCode(data));
    }
  }

  resetForm() {
    this.countyzipcodes.controls = [];
    this.formCounty.reset();
  }

  createGrid() {
    this.gridZips = <GridOptions> {
      enableSorting: true,
      enableFilter: true,
      sortingOrder: ['desc', 'asc', null],
      enableColResize: true,
      columnDefs: [
        {
          headerName: 'Zip Codes',
          field: 'zipcode',
          colId: 'zipcode',
        },
        {
          colId: 'deleteRow',
          cellRenderer: () => {
            return "<span <i class='fa fa-trash-o' style='color: red; cursor: pointer;' aria-hidden='true'></i></span>";
          },
          width: 50,
        },
      ],
    };
  }

  selectZipCode(params: any) {
    if (params.colDef.colId === 'deleteRow') {
      if (this.countyzipcodes.length > 1) {
        this.sweetAlertHelper.createCustomAlert({
          title: 'Are you sure?',
          text: `Disable this zipcode ${params.data.zipcode}?`,
          type: 'info',
          showCancelButton: true,
          confirmButtonColor: '#3085d6',
          cancelButtonColor: '#d33',
          confirmButtonText: 'Yes',
        }).then((result) => {
          if (result.value) {
            this.removeZipCode(params.rowIndex);
          }
        });
      } else {
        this.sweetAlertHelper.createCustomAlert({
          title: 'Error',
          text: `If the county has only one zip code, you can't delete it`,
          type: 'error',
          showCancelButton: false,
          confirmButtonColor: '#3085d6',
          cancelButtonColor: '#d33',
          confirmButtonText: 'Ok',
        }).then();
      }
    }
  }

  getCounty(countyId: number) {
    this.subscriptions.push(
      this.serviceCountyService
        .getCountyById(countyId)
        .subscribe((data: any) => {
          if (data.countyid) {
            this.formCounty.patchValue({
              countyid: data.countyid,
              description: data.description,
              region: data.region,
              letterregion: data.letterregion,
              isactive: data.isactive,
              countyzipcodes: [],
            });
            data.countyzipcodes.forEach((element: any) => {
              if (element.countyzipcodeid) {
                this.countyzipcodes.push(this.newzipCode(element));
              }
            });
          }
        })
    );
  }

  saveAction() {
    const formData = Object.assign({}, this.formCounty.getRawValue());
    if (formData.countyid) {
      this.loading.show();
      this.subscriptions.push(
        this.serviceCountyService.putCounty(formData).subscribe(
          (resp) => {
            this.loading.hiden();
            if (resp.data.length > 0) {
              this.mesageError(resp.data);
            } else {
              this.sweetAlertHelper.createCustomAlert({
                title: 'Success',
                text: resp.message,
                type: 'success',
              }).then((result: any) => {
                if (result.value) {
                  this.getCountyData();
                }
              });
            }
          },
          (error) => {
            this.loading.hiden();
            this.sweetAlertHelper.createCustomAlert({
              title: 'Error',
              text: error.message,
              type: 'error',
            });
          }
        )
      );
    } else {
      this.loading.show();
      this.subscriptions.push(
        this.serviceCountyService.postCounty(formData).subscribe(
          (resp) => {
            if (resp.success) {
              this.loading.hiden();
              this.sweetAlertHelper.createCustomAlert({
                title: 'Success',
                text: resp.message,
                type: 'success',
              }).then((result: any) => {
                if (result.value) {
                  this.cancelCreateEdit();
                }
              });
            }
          },
          (error) => {
            this.loading.hiden();
            this.sweetAlertHelper.captureException(error);
          }
        )
      );
    }
  }

  mesageError(error: any) {
    const countyErros = [];
    for (const i in error) {
      countyErros.push(`<br>${i} = ${error[i]}`);
    }
    const message = this.sweetAlertHelper.createCustomAlert({
      title: ``,
      html: `<div style="max-height:300px; overflow:auto">
        <h3>County Errors:</h3> ${countyErros}
       </div>`,
      type: 'error'
    });
    return message;
  }


  cancelCreateEdit() {
    this.cancel.emit(true);
  }

  getRowStyle(params: any): any {
    if (params.data.countyzipcodeid === 0) {
      return {
        'background-color': '#BDECB6',
        color: 'black',
      };
    }
  }

  removeZipCode(index: number) {
    this.countyzipcodes.removeAt(index);
  }

  ngOnDestroy() {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }
}



// WEBPACK FOOTER //
// ./src/app/pages/county/components/create-county/create-county.component.ts